<template>
    <div>
        <cell-loader v-if="value.loading" />

        <div
            v-else
            class="uttr-wrap"
            :class="isEdit ? 'highlighted' : ''"
        >
            <div
                v-click-outside="clearSelected"
                class="sequence"
            >
                <div class="editor-wrap">
                    <autosize-textarea
                        v-if="isEdit"
                        v-model="editText"
                        :placeholder="$t('common.utterance.utterance.writeAPhrase')"
                        :maxlength="maxlength"
                    />
                    <autosize-textarea
                        v-else-if="autogenerated"
                        v-model="$props.value.text"
                        :placeholder="$t('common.utterance.utterance.writeAPhrase')"
                        :readonly="true"
                        :maxlength="maxlength"
                    />

                    <div v-else class="word-break">
                        <entity-token
                            v-for="(token, $index) in tokens"
                            :key="$index"
                            :index="$index"
                            :token="token"
                            :entities="entities.entities"
                            :entityPredictions="entities.autoPredictedEntities"
                            :selected-token.sync="selectedToken"
                            @add-entity="handleAddEntity"
                            @set-entity-role="setEntityRole"
                            @remove-entity="removeEntity"
                        />
                    </div>
                </div>
                <div class="right">
                    <div
                        v-if="isEdit"
                        class="btns-wrap"
                    >
                        <or-icon-button
                            class="flat"
                            icon="close"
                            type="secondary"
                            size="small"
                            @click="noBtnHandler"
                        />
                        <or-icon-button
                            class="flat"
                            icon="check"
                            type="secondary"
                            size="small"
                            :disabled="confirmBtnIsDisbaled"
                            @click="onConfirm"
                        />
                    </div>
                    <or-icon-button
                        v-else
                        has-dropdown
                        disable-ripple
                        class="flat"
                        ref="dropdownButton"
                        icon="more_vert"
                        size="small"
                        type="secondary"
                    >
                        <or-menu
                            contain-focus
                            has-icons
                            slot="dropdown"
                            :options="menuOptions"
                            @select="onSelect"
                            @close="$refs.dropdownButton.closeDropdown()"
                        />
                    </or-icon-button>
                </div>
                <or-confirm
                    ref="deleteConfirm"
                    :title="$t('common.utterance.utterance.deleteConfirmation.title')"
                    @confirm="deleteQuestion"
                >
                  {{ $t('common.utterance.utterance.deleteConfirmation.description') }}
                </or-confirm>
                <or-confirm
                    ref="editConfirm"
                    :title="$t('common.utterance.utterance.editConfirmation.title')"
                    @confirm="editQuestion"
                >
                  {{ $t('common.utterance.utterance.editConfirmation.description') }}
                </or-confirm>
                <or-confirm
                    ref="addMlEntityConfirm"
                    :title="$t('common.utterance.utterance.addMlEntityConfirmation.title')"
                    @confirm="addEntity(newEntity)"
                >
                  {{ $t('common.utterance.utterance.addMlEntityConfirmation.description') }}
                </or-confirm>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import directives from './directives'
import KnowledgeBus from '@/KnowledgeBus'

import { MachineLearningLabelingService } from '@/services/utterance/machine-learning-labeling.service'
import { table_limits as tableLimits } from '@/modules/limits'
import EntityToken from './entity-token/entity-token'
import autosizeTextarea from '../autosize_textarea'
import cellLoader from '../../table/table_stuff/cell_loader'
import { ENTITY_TYPE } from '@/components/model/nlu-model/sidebar/entities/entity-types'
import { MachineLearningTestPreviewService } from '@/services/utterance/machine-learning-test-preview.service'

function indexToToken (text, index) {
  const tokens = text
    .slice(0, index + 1)
    .match(/(([^\x00-\x7F]|\w)+|\S)\s*/giu) || [] // eslint-disable-line no-control-regex

  return tokens.length - 1
}

export default {
  directives,

  components: {
    EntityToken,
    autosizeTextarea,
    cellLoader
  },

  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    autogenerated: {
      type: Boolean,
      default: false
    },
    intentType: String,
    intent: String,
    application: String
  },

  created () {
    this.mlLabelingService = new MachineLearningLabelingService()
    const tokens = MachineLearningLabelingService.extractTokensFromText(this.localValue.text)
    this.mlLabelingService.updateTokens(tokens)
    this.localValue.entities.forEach(mlEntity => {
      this.mlLabelingService.addEntity(mlEntity)
    })
  },

  computed: {
    mlEntities () {
      return this.localValue.entities
    },

    localValue () {
      return this.value
    },

    confirmBtnIsDisbaled () {
      return !_.inRange(this.editText.length, tableLimits.utterance.min, tableLimits.utterance.max + 2)
    },

    maxlength () {
      return tableLimits.utterance.max + 1
    },

    localEntityPredictions () {
      return _.get(this.value, 'entityPredictions', [])
    },

    entities () {
      const transformMlEntity = mlEntity => ({
        entity: mlEntity.entity,
        start: indexToToken(this.value.text, mlEntity.startPos),
        end: indexToToken(this.value.text, mlEntity.endPos),
        type: ENTITY_TYPE.machineLearning,
        children: mlEntity.children ? mlEntity.children.map(child => transformMlEntity(child)) : [],
        role: mlEntity.role
      })

      const machineLearningEntities = this.mlEntities.map(el => transformMlEntity(el))
      const autoPredictedEntities = []
      const entityPredictions = []

      this.localEntityPredictions.forEach(el => {
        if (this.isMlEntity(el)) {
          autoPredictedEntities.push(transformMlEntity(el))
        } else {
          entityPredictions.push({
            entity: el.entityName,
            start: el.startTokenIndex,
            end: el.endTokenIndex,
            role: el.role,
            type: 'auto',
            hasPositionIndex: _.has(el, 'startPos') && _.has(el, 'endPos')
          })
        }
      })

      return {
        entities: [...machineLearningEntities, ...entityPredictions],
        autoPredictedEntities
      }
    },

    tokens () {
      const tokens = MachineLearningLabelingService.extractTokensFromText(this.localValue.text)
      this.mlLabelingService.updateTokens(tokens)
      this.mlEntities.forEach(mlEntity => {
        this.mlLabelingService.addEntity(mlEntity)
      })
      return tokens
    },

    menuOptions () {
      const hasPredictedMLEntities = this.localEntityPredictions.some(entity => entity.type === ENTITY_TYPE.machineLearning)
      const options = [{
        id: 'edit',
        label: this.$t('common.utterance.utterance.actions.editPhrase'),
        icon: 'edit'
      }, {
        id: 'delete',
        label: this.$t('common.utterance.utterance.actions.deletePhrase'),
        icon: 'delete'
      }, {
        id: 'suggested',
        label: this.$t('common.utterance.utterance.actions.seeSuggested'),
        icon: 'subject'
      }
      ]
      if (hasPredictedMLEntities) {
        options.unshift({
          id: 'confirm-entity-predictions',
          label: this.$t('common.utterance.utterance.actions.confirmEntity'),
          icon: 'done',
          disabled: !hasPredictedMLEntities
        })
      }

      return options
    }
  },

  data () {
    return {
      isEdit: false,
      editText: '',
      newEntity: null,
      selectedToken: {
        start: null,
        end: null
      }
    }
  },

  methods: {
    isMlEntity (entity) {
      return entity.type === ENTITY_TYPE.machineLearning
    },

    onSelect (option) {
      switch (option.id) {
        case 'edit':
          if (_.isEmpty(this.entities.entities)) {
            this.editQuestion()
          } else {
            this.$refs.editConfirm.open()
          }
          break

        case 'delete':
          this.$refs.deleteConfirm.open()
          break

        case 'confirm-entity-predictions': {
          this.confirmMlEntityPredictions()
          break
        }

        case 'suggested':
          KnowledgeBus.$emit('phrases-recommendations::show', {
            ...this.value,
            intentType: this.$props.intentType,
            intent: this.$props.intent,
            application: this.$props.application
          })
      }
    },

    confirmMlEntityPredictions () {
      const utterance = _.cloneDeep(this.value)
      utterance.entities = [
        ...utterance.entities,
        ...MachineLearningTestPreviewService.removeForbiddenParams(
          utterance.entityPredictions
            .filter(entity => entity.type === ENTITY_TYPE.machineLearning)
        )
      ]
      utterance.entityPredictions = utterance.entityPredictions
        .filter(entity => entity.type !== ENTITY_TYPE.machineLearning)

      this.$emit('update-question', utterance)
    },

    noBtnHandler () {
      this.isEdit = false
      this.$emit('show-add-btn')
    },

    onConfirm () {
      this.isEdit = false
      this.$emit('show-add-btn')

      const utterance = _.cloneDeep(this.value)
      utterance.text = _.trim(this.editText)
      utterance.entities = []
      utterance.entityPredictions = []

      this.$emit('update-question', utterance, true)
    },

    editQuestion () {
      this.$emit('hide-add-btn')

      this.isEdit = !this.isEdit
      this.editText = this.localValue.text
    },

    deleteQuestion () {
      this.$emit('delete-question', this.localValue)
    },

    handleAddEntity (entity) {
      const entityPredictions = _.get(this.value, 'entityPredictions', [])

      if (entityPredictions.some(entity => entity.type === ENTITY_TYPE.machineLearning)) {
        this.newEntity = entity
        this.$refs.addMlEntityConfirm.open()
      } else {
        this.addEntity(entity)
      }
    },

    addEntity (entity) {
      this.newEntity = null
      const entityWithPosition = (newEntity, start, end) => ({
        ...newEntity,
        children: newEntity.children.map(child => entityWithPosition(child, start, end)),
        startPos: this.tokens[start].start,
        endPos: this.tokens[end].end
      })
      const { start, end } = this.selectedToken
      const utterance = _.cloneDeep(this.value)
      const newEntity = entityWithPosition(entity, start, end)

      this.mlLabelingService.addEntity(newEntity)
      utterance.entities = this.mlLabelingService.getEntities()
      // Removing all autopredicted entities to avoid inconsistencies
      utterance.entityPredictions = (utterance.entityPredictions || []).filter(entity => entity.type !== ENTITY_TYPE.machineLearning)

      this.selectedToken = { start: null, end: null }
      this.$emit('update-question', utterance)
    },

    setEntityRole ({ entity: entityName, start, end, type }, role) {
      const utterance = _.cloneDeep(this.value)
      const startPos = this.tokens[start].start
      const endPos = this.tokens[end].end

      if (type === ENTITY_TYPE.machineLearning) {
        utterance.entities = this.mlEntities.map(entity => {
          const isSelectedMLEntity = _.isMatch(entity, { entity: entityName, startPos, endPos })
          if (isSelectedMLEntity) {
            return {
              ...entity,
              role
            }
          }

          return entity
        })
      } else {
        utterance.entityPredictions = (utterance.entityPredictions || []).map(entity => {
          const isSelectedEntity = _.isMatch(entity, { entityName, startTokenIndex: start, endTokenIndex: end })
          if (isSelectedEntity) {
            return {
              ...entity,
              role
            }
          }

          return entity
        })
      }

      this.$emit('update-question', utterance)
    },

    removeEntity (entity) {
      const utterance = _.cloneDeep(this.value)
      if (entity.isConfirmed) {
        this.mlLabelingService.removeEntity(entity)
        utterance.entities = this.mlLabelingService.getEntities()
      } else {
        const startPos = this.tokens[entity.start].start
        const endPos = this.tokens[entity.end].end
        const removeEntity = entities => entities.reduce((acc, curr) => {
          if (curr.startPos <= startPos && curr.endPos >= endPos) {
            if (curr.entity !== entity.entity) {
              acc.push(curr)
              curr.children = removeEntity(curr.children)
            }
          } else {
            acc.push(curr)
          }
          return acc
        }, [])

        utterance.entityPredictions = removeEntity(utterance.entityPredictions)
      }
      this.$emit('update-question', utterance)
    },

    clearSelected () {
      if (this.selectedToken.start !== null) {
        this.selectedToken = { start: null, end: null }
      }
    }
  },

  watch: {
    localValue (value) {
      this.mlLabelingService.reset()
      const tokens = MachineLearningLabelingService.extractTokensFromText(value.text)
      this.mlLabelingService.updateTokens(tokens)
      this.mlEntities.forEach(mlEntity => {
        this.mlLabelingService.addEntity(mlEntity)
      })
    },

    mlEntities (value) {
      this.mlLabelingService.reset()
      value.forEach(mlEntity => {
        this.mlLabelingService.addEntity(mlEntity)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
    .uttr-wrap {
        &.highlighted {
            border: solid 1px $or-blue;
        }
    }

    .sequence {
        display: flex;
        font-size: 14px;
        cursor: pointer;

        .editor-wrap {
            display: flex;
            flex-grow: 1;
            padding: $sp-2;
        }

        .right {
            display: flex;
            align-items: flex-start;
            justify-content: flex-end;
            min-width: 60px;
        }

        .btns-wrap {
            display: flex;
            justify-content: center;
            height: 100%;
        }

        .word-break {
            word-break: break-all;
        }
    }
</style>

<style lang="scss">
    .editor-wrap {
        .ui-textbox {
            margin-bottom: 0;
            flex-grow: 1;
        }
    }
</style>
